import upperFirst from 'lodash/upperFirst';
import orderBy from 'lodash/orderBy';
import getYear from 'date-fns/getYear';
import getMonth from 'date-fns/getMonth';

import { formatDate } from 'shared/functions';
import { creditRatingRangeMapping } from 'shared/constants/creditRatingRangeValues';
import { creditRatingScalesMapping, creditRatingScalesMap } from 'shared/mappings';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { summaryTabCreditRatingNamesPriorites } from 'shared/constants/summaryTab';
import { longDateFormat } from 'shared/constants/dateFormat';

const orderByPriority = (data) =>
  orderBy(
    data,
    [
      ...summaryTabCreditRatingNamesPriorites.map(
        (name) => (item) => item.latestRating.name === name,
      ),
      (item) => item.latestRating.ratingDate,
    ],
    'desc',
  );

const parseValue = (value, array) => array.find((item) => item.label === value).value;
const parseLabels = (value) => `${getYear(new Date(value))}/${getMonth(new Date(value)) + 1}`;
const parseHistory = (ratingHistory, ratingRange) => {
  return ratingHistory.data.length !== 0
    ? ratingHistory.data
        .map((value, index) => {
          const ratingsByName = creditRatingRangeMapping(portfolioTypes.CREDIT_RATING, ratingRange);

          const dataExist = ratingsByName.some((item) => item.label === value);

          return (
            dataExist && {
              data: parseValue(value, ratingsByName),
              label: parseLabels(ratingHistory.labels[index]),
            }
          );
        })
        .filter((item) => item)
    : [];
};

const setParticipationType = ({ demand, participationType }) => {
  if (participationType) {
    return upperFirst(participationType);
  }
  if (demand && demand.toLowerCase() === 'solicited') {
    return 'Participation not specified';
  }

  return 'Without entity participation';
};

const commonCreditRatingParser = ({
  ratingHistory,
  ratingRange,
  latestRating,
  committeeChair,
  productCategory,
  demand,
  participationType,
}) => ({
  dates: {
    lastRatingAction: formatDate(ratingHistory.lastRatingAction),
    firstRating: formatDate(ratingHistory.firstRatingAction),
  },
  documents: [
    {
      name: 'methodologies',
      path: 'documentsModal',
      title: 'Methodologies',
    },
    {
      name: 'reports',
      path: 'documentsModal',
      title: 'Rating docs',
    },
  ],
  generalInformation: {},
  generalInformationList: [
    latestRating.name,
    latestRating.ratingClassification,
    demand,
    setParticipationType({ demand, participationType }),
  ],
  ratingScalesData: creditRatingScalesMapping(portfolioTypes.CREDIT_RATING, ratingRange),
  latestChange: {
    status: latestRating.ratingAction,
    date: formatDate(latestRating.ratingDate, longDateFormat),
  },
  levels: {
    level: productCategory,
    title: 'Credit rating',
  },
  scoreTitle: latestRating.name,
  score: latestRating.rating,
  outlook: latestRating.outlook,
  businessCards: [
    {
      title: 'Lead analyst',
      name: `${latestRating.leadAnalyst.firstName} ${latestRating.leadAnalyst.name}`,
      position: latestRating.leadAnalyst.position,
      id: latestRating.leadAnalyst.id,
    },
    {
      title: 'Committee chair',
      name: `${committeeChair.firstName} ${committeeChair.lastName}`,
      position: committeeChair.position,
      id: committeeChair.id,
    },
  ],
  history: parseHistory(ratingHistory, ratingRange),
  ratingScale: creditRatingScalesMap[portfolioTypes.CREDIT_RATING][ratingRange],
  endorsementList: [
    {
      label: 'Location',
      value: `${latestRating.issuanceLocation || 'EU'} rated`,
    },
    {
      label: 'Endorsement',
      value: latestRating.endorsementLocations
        ? latestRating.endorsementLocations.join(', ')
        : 'None',
    },
  ],
  isPublic: productCategory === 'L5',
});

export const summaryCardMapCreditRatingParser = (data) =>
  orderByPriority(data)
    .filter(Boolean)
    .map(
      ({
        ratingHistory,
        ratingRange,
        latestRating,
        committeeChair,
        productCategory,
        demand,
        participationType,
        endorsementLocations,
        issuanceLocation,
      }) =>
        commonCreditRatingParser({
          ratingHistory,
          ratingRange,
          latestRating,
          committeeChair,
          productCategory,
          demand,
          participationType,
          endorsementLocations,
          issuanceLocation,
        }),
    );
